import type { FeatureFlags, ServerConfiguration } from '@/domain/configuration';
import { BrowsingGateway } from '@/gateways/BrowsingGateway';
import type { PagePromptsHolder } from '@/lib/beacon/pagePrompts';
import type { PageViewTracker } from '@/lib/beacon/pageView';
import { createPageViewTracker } from '@/lib/beacon/pageView';
import { pageShowsEntryPoint, visibleLauncherIds } from '@/lib/beacon/visibleLauncherIds';
import { getBrowsingUrl } from '@/lib/getBrowsingUrl';
import { logger } from '@/lib/logger';

export interface StartPageViewsOptions {
  appId: string;
  clientId: string;
  featureFlags: FeatureFlags | undefined;
  pagePrompts: PagePromptsHolder;
  serverConfig: ServerConfiguration;
}

/**
 * Sends a page view on load, and returns the tracker so the caller can send again when the
 * host reports a navigation.
 *
 * Separate from `startBeaconPipeline` on purpose: gating on its WebMCP manifest would
 * exclude every org running page prompts without one.
 */
export function startPageViews({
  appId,
  clientId,
  featureFlags,
  pagePrompts,
  serverConfig,
}: StartPageViewsOptions): PageViewTracker | null {
  if (!clientId) {
    logger.warn('[pageView] skipped: no clientId');
    pagePrompts.disarm();
    return null;
  }

  const gateway = new BrowsingGateway({ endpoint: getBrowsingUrl(serverConfig), orgId: serverConfig.orgId });
  let sent = false;
  const tracker = createPageViewTracker({
    appId,
    clientId,
    getLauncherIds: () => visibleLauncherIds(serverConfig, featureFlags),
    pageShowsEntryPoint: () => pageShowsEntryPoint(serverConfig, featureFlags),
    send: (payload) => {
      sent = true;
      const request = pagePrompts.begin(payload.pageIdentity);
      void gateway
        .sendPageView(payload)
        .then((result) => {
          // Logged as well, so the round trip is visible on a real page.
          logger.log('[pageView] beacon answered', result);
          pagePrompts.settle(request, result);
        })
        // The gateway swallows its own failures; this catches a future one escaping, which
        // would reach the brand's page as an unhandled rejection.
        .catch((error: unknown) => {
          logger.warn('[pageView] beacon answer dropped', error);
          pagePrompts.settle(request, null);
        });
    },
  });

  // Sent after the tracker exists, and guarded: the caller reads a throw here as this
  // never having started, and would then report nothing for the rest of the session.
  try {
    tracker.sendIfChanged();
  } catch (error) {
    logger.error('[pageView] first page view failed', error);
  }
  // Nothing sent means no answer coming; left armed, the holder would keep a launcher waiting.
  if (!sent) pagePrompts.disarm();
  return tracker;
}
